import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { LetDirective, PushPipe } from '@ngrx/component';
import {
  SharedActions,
  UserActions,
  UserSettingsActions,
} from '../../data-access/state/user-account-settings.actions';
import { select, Store } from '@ngrx/store';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserAccountSettingsSelectors } from '../../data-access/state/user-account-settings.selector-types';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  EnableEscCloseDirective,
  UiButtonComponent,
  UiPhoneInputComponent,
} from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-verify-phone-modal',
  standalone: true,
  imports: [
    AsyncPipe,
    NgIf,
    PushPipe,
    LetDirective,
    TranslatePipe,
    MatDialogClose,
    ReactiveFormsModule,
    UiPhoneInputComponent,
    UiButtonComponent,
    UiButtonComponent,
  ],
  templateUrl: './verify-phone-modal.component.html',
  styleUrl: './verify-phone-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class VerifyPhoneModalComponent implements OnInit {
  private store$ = inject(Store);
  private dialogRef = inject(MatDialogRef<VerifyPhoneModalComponent>);
  private actions$ = inject(Actions);
  public data = inject<{ phone: string; isVerified: boolean }>(MAT_DIALOG_DATA);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  public selectIsSaving$: Observable<boolean> = this.store$.pipe(
    select(UserAccountSettingsSelectors.selectIsSaving),
  );
  public showSuccess$ = new BehaviorSubject<boolean>(false);

  public phoneControl = new FormControl<string>('', [
    Validators.required.bind(Validators),
  ]);
  public codeSentId$ = new BehaviorSubject<string | null>(null);
  public readonly countDown = 60;

  public confirmationCodeControl = new FormControl<string>('', [
    Validators.required.bind(Validators),
    Validators.pattern('^[0-9]*$'),
    Validators.minLength(6),
    Validators.maxLength(6),
  ]);

  public resendTimer$ = new BehaviorSubject<number>(this.countDown);

  public ngOnInit(): void {
    if (this.data.phone) {
      this.phoneControl.patchValue(this.data.phone);
    }
    this.actions$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(UserActions.changePhoneRequestSuccess))
      .subscribe((res) => {
        this.codeSentId$.next(res.id);
        this.startTimer();
      });

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(UserSettingsActions.confirmCodeSuccess),
      )
      .subscribe(() => {
        this.store$.dispatch(
          SharedActions.showMessage({
            success: String(
              this.translateService.instant('TOASTS.PHONE_CHANGED_SUCCESS'),
            ),
          }),
        );
        this.dialogRef.close();
      });
  }

  public sendCode(): void {
    const phone = this.phoneControl.value ?? '';
    if (!phone) return;
    this.store$.dispatch(UserActions.startChangePhoneProcess({ phone }));
  }

  public confirmCode(): void {
    const codeValue = this.confirmationCodeControl.value ?? '0';
    const verificationId = this.codeSentId$.getValue();
    if (!verificationId) return;
    this.store$.dispatch(
      UserSettingsActions.startConfirmCode({
        code: Number(codeValue),
        id: verificationId,
      }),
    );
  }

  public close(): void {
    this.dialogRef.close();
  }

  public startTimer(): void {
    let totalTime = this.countDown;
    const timerInterval = setInterval(() => {
      totalTime = totalTime - 1;
      if (!totalTime) {
        clearInterval(timerInterval);
      }
      this.resendTimer$.next(totalTime);
    }, 1000);
  }
}
